import React, { useState } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  IconButton,
  InputAdornment,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import ModalBase from "../../../../components/ui/ModalBase";
import { useToastContext } from "../../../../context/ToastContextProvider";
import { DatePicker } from "../../../../components/shared/DatePicker";
import { workExperienceDateSx } from "../../style";
import colors from "../../../../shared/constants/color";
import axios from "axios";
import { SERVICEURL } from "../../../../appconfig";
import {
  getWorkExperienceDetail,
  updateWorkExperience,
} from "../../../../api/workExperienceApi";

export interface WorkExperienceListItem {
  companyName: string;
  positionName: string;
  startDate: string;
  endDate: string | null;
  statement: string | null;
  consultantComments: string | null;
  usedTechnologies: string | null;
  reasonForLeave: string | null;
  isEnglish?: boolean | null;
}

interface AddWorkExperienceModalProps {
  open: boolean;
  memberId: string;
  workExperienceId?: string | null;
  onClose: () => void;
  onSuccess?: (item: WorkExperienceListItem) => void;
}

const initialFormState = {
  companyName: "",
  position: "",
  startYear: "",
  endYear: "",
  startDate: null as Date | null,
  endDate: null as Date | null,
  language: "",
  description: "",
  technologies: "",
  consultantComment: "",
  leavingReason: "",
  isCurrent: false,
};

const AddWorkExperienceModal: React.FC<AddWorkExperienceModalProps> = ({
  open,
  memberId,
  workExperienceId = null,
  onClose,
  onSuccess,
}) => {
  const isEditMode = !!workExperienceId;
  const { showToast } = useToastContext();
  const [form, setForm] = useState<typeof initialFormState>(initialFormState);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  React.useEffect(() => {
    console.log("AddWorkExperienceModal useEffect - open:", open, "workExperienceId:", workExperienceId);
    
    if (!open) {
      // Modal kapandığında state'leri temizle
      resetState();
      return;
    }

    const loadData = async () => {
      if (workExperienceId) {
        console.log("Loading work experience data for ID:", workExperienceId);
        await loadWorkExperienceData(workExperienceId);
      } else {
        console.log("Resetting form for new work experience");
        resetState();
      }
    };

    loadData();
  }, [open, workExperienceId]);

  const loadWorkExperienceData = async (id: string) => {
    try {
      const data = await getWorkExperienceDetail(id);
      console.log("Loaded work experience data:", data);

      // Parse dates
      const parseDate = (dateStr: string | null): Date | null => {
        if (!dateStr) return null;
        const date = new Date(dateStr);
        return isNaN(date.getTime()) ? null : date;
      };

      // Determine language
      let language = "";
      if (data.isEnglish === true) {
        language = "EN";
      } else if (data.isEnglish === false) {
        language = "TR";
      }

      setForm({
        companyName: data.companyName || "",
        position: data.positionName || "",
        startYear: data.startDate ? new Date(data.startDate).getFullYear().toString() : "",
        endYear: data.endDate ? new Date(data.endDate).getFullYear().toString() : "",
        startDate: parseDate(data.startDate),
        endDate: parseDate(data.endDate),
        language: language,
        description: data.statement || "",
        technologies: data.usedTechnologies || "",
        consultantComment: data.consultantComments || "",
        leavingReason: data.reasonForLeave || "",
        isCurrent: !data.endDate,
      });

      console.log("Form set with:", {
        companyName: data.companyName,
        position: data.positionName,
        language: language,
      });
    } catch (error) {
      console.error("Error loading work experience data:", error);
      showToast("İş tecrübesi bilgisi yüklenirken bir hata oluştu.", "error");
    }
  };

  const resetState = () => {
    setForm(initialFormState);
    setErrors({});
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const validate = () => {
    const newErrors: Record<string, string> = {};
    const currentYear = new Date().getFullYear();

    if (!form.companyName.trim()) {
      newErrors.companyName = "Firma adı zorunludur.";
    }
    if (!form.position.trim()) {
      newErrors.position = "Pozisyon bilgisi zorunludur.";
    }
    if (!form.language) {
      newErrors.language = "Dil seçimi zorunludur.";
    }
    if (!form.startYear) {
      newErrors.startYear = "Giriş tarihi zorunludur.";
    } else {
      const year = parseInt(form.startYear, 10);
      if (isNaN(year) || year < 1900 || year > currentYear) {
        newErrors.startYear = `Giriş tarihi 1900 ile ${currentYear} arasında olmalıdır.`;
      }
    }

    if (form.endYear) {
      const year = parseInt(form.endYear, 10);
      if (isNaN(year) || year < 1900 || year > currentYear) {
        newErrors.endYear = `Çıkış tarihi 1900 ile ${currentYear} arasında olmalıdır.`;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSaveClick = () => {
    if (!validate()) return;
    setConfirmOpen(true);
  };

  const handleSubmit = async () => {
    setConfirmOpen(false);

    const formatDate = (date: Date | null): string | null => {
      if (!date || isNaN(date.getTime())) return null;
      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    };

    const formatDateForApi = (date: Date | null): string | null => {
      if (!date || isNaN(date.getTime())) return null;
      return date.toISOString();
    };

    try {
      setIsSubmitting(true);

      const isEnglish =
        form.language === "EN"
          ? true
          : form.language === "TR"
          ? false
          : false;

      if (isEditMode && workExperienceId) {
        // Düzenleme modu - PUT isteği
        const updatePayload = {
          workExperienceID: workExperienceId,
          memberId: memberId,
          companyName: form.companyName.trim(),
          positionName: form.position.trim(),
          startDate: formatDateForApi(form.startDate),
          endDate: formatDateForApi(form.endDate),
          statement: form.description?.trim() || "",
          consultantComments: form.consultantComment?.trim() || "",
          usedTechnologies: form.technologies?.trim() || "",
          reasonForLeave: form.leavingReason?.trim() || "",
          isEnglish: isEnglish,
        };

        console.log("Update work experience payload:", updatePayload);
        await updateWorkExperience(updatePayload);
        showToast("İş tecrübesi başarıyla güncellendi.", "success");
        if (onSuccess) {
          onSuccess({} as WorkExperienceListItem);
        }
        handleClose();
      } else {
        // Ekleme modu - POST isteği
        const token = localStorage.getItem("token");

        const payload = {
          MemberID: memberId,
          CompanyName: form.companyName.trim() || null,
          PositionName: form.position.trim() || null,
          StartDate: formatDate(form.startDate),
          EndDate: formatDate(form.endDate),
          Statement: form.description?.trim() || null,
          ConsultantComments: form.consultantComment?.trim() || null,
          UsedTechnologies: form.technologies?.trim() || null,
          ReasonForLeave: form.leavingReason?.trim() || null,
          IsEnglish: isEnglish,
        };

        const response = await axios.post(
          `${SERVICEURL}api/Candidates/AddWorkExperience`,
          payload,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );

        const apiData = response.data?.data as WorkExperienceListItem | undefined;

        if (apiData && onSuccess) {
          onSuccess(apiData);
        }

        showToast("İş tecrübesi başarıyla eklendi.", "success");
        handleClose();
      }
    } catch (error) {
      console.error("Work experience save error:", error);
      const errorMessage = isEditMode
        ? "İş tecrübesi güncellenirken bir hata oluştu."
        : "İş tecrübesi eklenirken bir hata oluştu.";
      showToast(errorMessage, "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
    <ModalBase
      open={open}
      onClose={handleClose}
      title={isEditMode ? "İş Tecrübesi Düzenle" : "İş Tecrübesi Ekle"}
      actions={
        <>
          <Button
            variant="outlined"
            onClick={handleClose}
            sx={{ borderRadius: 2, textTransform: "none", minWidth: 80 }}
          >
            İptal
          </Button>
          <Button
            variant="contained"
            sx={{ borderRadius: 2, minWidth: 80 }}
            disabled={isSubmitting}
            onClick={handleSaveClick}
          >
            {isSubmitting ? "Kaydediliyor..." : "Kaydet"}
          </Button>
        </>
      }
      maxWidth="md"
      minWidth={800}
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2, width: "100%" }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "2fr 1fr 1fr",
            gap: 2,
            width: "100%",
          }}
        >
          <TextField
            fullWidth
            label="Firma Adı*"
            name="companyName"
            value={form.companyName}
            onChange={handleChange}
            error={!!errors.companyName}
            helperText={errors.companyName}
            size="small"
            InputProps={{
              endAdornment:
                form.companyName !== "" ? (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      sx={{
                        width: 24,
                        height: 24,
                        borderRadius: "50%",
                        "&:hover": { backgroundColor: colors.neutral.N1200 },
                      }}
                      onClick={() =>
                        setForm((prev) => ({
                          ...prev,
                          companyName: "",
                        }))
                      }
                    >
                      ✕
                    </IconButton>
                  </InputAdornment>
                ) : null,
            }}
          />

          {/* Giriş Tarihi - DatePicker */}
          <Box sx={{ display: "flex", flexDirection: "column", width: "100%" }}>
            <DatePicker
              label="Giriş Tarihi*"
              value={form.startDate}
              sx={workExperienceDateSx}
              externalError={!!errors.startYear}
              onChange={(newValue: Date | null) => {
                setForm((prev) => ({
                  ...prev,
                  startDate: newValue,
                  startYear:
                    newValue && !isNaN(newValue.getTime())
                      ? newValue.getFullYear().toString()
                      : "",
                }));
              }}
              fullWidth
            />
            {errors.startYear && (
              <Typography
                variant="caption"
                color="error"
                sx={{ mt: 0.5, ml: 0.5 }}
              >
                {errors.startYear}
              </Typography>
            )}
          </Box>

          {/* Çıkış Tarihi - DatePicker */}
          <Box sx={{ display: "flex", flexDirection: "column", width: "100%" }}>
              <DatePicker
                label="Çıkış Tarihi"
                value={form.endDate}
                sx={workExperienceDateSx}
                externalError={!!errors.endYear}
                onChange={(newValue: Date | null) => {
                  setForm((prev) => ({
                    ...prev,
                    endDate: newValue,
                    endYear:
                      newValue && !isNaN(newValue.getTime())
                        ? newValue.getFullYear().toString()
                        : "",
                  }));
                }}
                fullWidth
              />
            {errors.endYear && (
              <Typography
                variant="caption"
                color="error"
                sx={{ mt: 0.5, ml: 0.5 }}
              >
                {errors.endYear}
              </Typography>
            )}
          </Box>
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "3fr 1fr",
            gap: 2,
            width: "100%",
          }}
        >
          <TextField
            fullWidth
            label="Pozisyon*"
            name="position"
            value={form.position}
            onChange={handleChange}
            error={!!errors.position}
            helperText={errors.position}
            size="small"
            InputProps={{
              endAdornment:
                form.position !== "" ? (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      sx={{
                        width: 24,
                        height: 24,
                        borderRadius: "50%",
                        "&:hover": { backgroundColor: colors.neutral.N1200 },
                      }}
                      onClick={() =>
                        setForm((prev) => ({
                          ...prev,
                          position: "",
                        }))
                      }
                    >
                      ✕
                    </IconButton>
                  </InputAdornment>
                ) : null,
            }}
          />

          <TextField
            select
            fullWidth
            label="Dil*"
            name="language"
            value={form.language}
            onChange={handleChange}
            size="small"
            error={!!errors.language}
            helperText={errors.language}
          >
            <MenuItem value="">Seçiniz</MenuItem>
            <MenuItem value="TR">TÜRKÇE</MenuItem>
            <MenuItem value="EN">İNGİLİZCE</MenuItem>
          </TextField>
        </Box>

        <TextField
          fullWidth
          label="Açıklama"
          name="description"
          value={form.description}
          onChange={handleChange}
          size="small"
          multiline
          minRows={3}
          InputProps={{
            endAdornment:
              form.description !== "" ? (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    sx={{
                      width: 24,
                      height: 24,
                      borderRadius: "50%",
                      "&:hover": { backgroundColor: colors.neutral.N1200 },
                    }}
                    onClick={() =>
                      setForm((prev) => ({
                        ...prev,
                        description: "",
                      }))
                    }
                  >
                    ✕
                  </IconButton>
                </InputAdornment>
              ) : null,
          }}
        />

        <TextField
          fullWidth
          label="Kullanılan Teknolojiler"
          name="technologies"
          value={form.technologies}
          onChange={handleChange}
          size="small"
          multiline
          minRows={3}
          InputProps={{
            endAdornment:
              form.technologies !== "" ? (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    sx={{
                      width: 24,
                      height: 24,
                      borderRadius: "50%",
                      "&:hover": { backgroundColor: colors.neutral.N1200 },
                    }}
                    onClick={() =>
                      setForm((prev) => ({
                        ...prev,
                        technologies: "",
                      }))
                    }
                  >
                    ✕
                  </IconButton>
                </InputAdornment>
              ) : null,
          }}
        />

        <TextField
          fullWidth
          label="Danışman Yorumu"
          name="consultantComment"
          value={form.consultantComment}
          onChange={handleChange}
          size="small"
          multiline
          minRows={3}
          InputProps={{
            endAdornment:
              form.consultantComment !== "" ? (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    sx={{
                      width: 24,
                      height: 24,
                      borderRadius: "50%",
                      "&:hover": { backgroundColor: colors.neutral.N1200 },
                    }}
                    onClick={() =>
                      setForm((prev) => ({
                        ...prev,
                        consultantComment: "",
                      }))
                    }
                  >
                    ✕
                  </IconButton>
                </InputAdornment>
              ) : null,
          }}
        />

        <TextField
          fullWidth
          label="İşten Ayrılma Nedeni"
          name="leavingReason"
          value={form.leavingReason}
          onChange={handleChange}
          size="small"
          multiline
          minRows={3}
          InputProps={{
            endAdornment:
              form.leavingReason !== "" ? (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    sx={{
                      width: 24,
                      height: 24,
                      borderRadius: "50%",
                      "&:hover": { backgroundColor: colors.neutral.N1200 },
                    }}
                    onClick={() =>
                      setForm((prev) => ({
                        ...prev,
                        leavingReason: "",
                      }))
                    }
                  >
                    ✕
                  </IconButton>
                </InputAdornment>
              ) : null,
          }}
        />

      </Box>
    </ModalBase>
    <Dialog
      open={confirmOpen}
      onClose={() => setConfirmOpen(false)}
      disableScrollLock
    >
      <DialogTitle>
        {isEditMode ? "İş Deneyimi Güncellensin mi?" : "İş Deneyimi Eklensin mi?"}
      </DialogTitle>
      <DialogContent>
        <Typography>
          {isEditMode 
            ? "İş Deneyimi güncellemek istediğinize emin misiniz?" 
            : "İş Deneyimi eklemek istediğinize emin misiniz?"}
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setConfirmOpen(false)} sx={{ textTransform: "none" }}>
          Hayır
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          sx={{ textTransform: "none", background: colors.blue.B800 }}
        >
          Evet
        </Button>
      </DialogActions>
    </Dialog>
    </>
  );
};

export default AddWorkExperienceModal;


